import { useEffect, useState } from "react";
import api from "../api/client";
import Icon from "./Icon";

// Detecta si un adjunto es previsualizable in-browser (imagen o PDF).
// Otros formatos (Word, Excel, etc.) se ofrecen para descarga sin preview.
export function isPreviewable(att) {
  const ct = (att?.content_type || "").toLowerCase();
  const name = (att?.filename || "").toLowerCase();
  if (ct.startsWith("image/") || ct === "application/pdf") return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg|pdf)$/i.test(name);
}

function kindOf(att) {
  const ct = (att?.content_type || "").toLowerCase();
  const name = (att?.filename || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return "image";
  if (ct === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  return "other";
}

// Modal de vista previa para un adjunto (imagen o PDF).
// - downloadUrl: url del endpoint autenticado que devuelve el blob
//   (ej. `/tasks/123/attachments/45/download`). Se descarga con axios (para
//   pasar el token) y se convierte a Object URL local que puede usar <img>/<iframe>.
export default function AttachmentPreview({ attachment, downloadUrl, onClose, footer, onPrev, onNext }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const kind = kindOf(attachment);

  useEffect(() => {
    if (!attachment || !downloadUrl) return;
    let alive = true;
    let objectUrl = null;
    setBlobUrl(null); setError(null);
    api.get(downloadUrl, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch((e) => alive && setError(e.response?.data?.detail || "No se pudo cargar la vista previa"));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, downloadUrl]);

  // Teclado: ESC cierra, ← → navegan (si hay onPrev/onNext).
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && onPrev) onPrev();
      else if (e.key === "ArrowRight" && onNext) onNext();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose, onPrev, onNext]);

  if (!attachment) return null;

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = attachment.filename;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-slate-900/90 backdrop-blur-sm" onClick={onClose}>
      <div className="flex items-center gap-3 border-b border-white/10 bg-slate-900/60 px-4 py-2 text-slate-100">
        <Icon name={kind === "image" ? "eye" : "paperclip"} size={16} className="shrink-0 text-slate-300" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{attachment.filename}</div>
          {attachment.size ? <div className="text-[11px] text-slate-400">{Math.round(attachment.size / 1024)} KB</div> : null}
        </div>
        <button onClick={(e) => { e.stopPropagation(); download(); }}
          className="inline-flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-xs text-slate-200 hover:bg-white/10">
          <Icon name="download" size={12} /> Descargar
        </button>
        <button onClick={onClose} aria-label="Cerrar"
          className="rounded-md p-1 text-slate-300 hover:bg-white/10">
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="relative flex-1 overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
        {onPrev && (
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Anterior"
            className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/70">
            <Icon name="chevron-down" size={22} className="rotate-90" />
          </button>
        )}
        {onNext && (
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Siguiente"
            className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/70">
            <Icon name="chevron-down" size={22} className="-rotate-90" />
          </button>
        )}
        {error ? (
          <div className="mx-auto mt-8 max-w-md rounded-lg bg-rose-900/40 p-4 text-sm text-rose-100">{error}</div>
        ) : !blobUrl ? (
          <div className="flex h-full items-center justify-center text-slate-400 text-sm">Cargando…</div>
        ) : kind === "image" ? (
          <div className="flex h-full items-center justify-center">
            <img src={blobUrl} alt={attachment.filename} className="max-h-full max-w-full rounded-lg shadow-2xl" />
          </div>
        ) : kind === "pdf" ? (
          <iframe title={attachment.filename} src={blobUrl} className="h-full w-full rounded-lg bg-white" />
        ) : (
          <div className="mx-auto mt-8 max-w-md rounded-lg bg-slate-800/60 p-4 text-sm text-slate-200">
            Este formato no admite vista previa. Descarga el archivo para verlo.
          </div>
        )}
      </div>
      {footer && (
        <div className="shrink-0 border-t border-white/10 bg-slate-900/70 px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
          {footer}
        </div>
      )}
    </div>
  );
}
